import { cn } from '@/lib/utils';
import { formatNotificationDate, isNotificationUnread } from '@/lib/notifications';
import type { AppNotificationDto } from '@/types/dto/notification.dto';
import { Bell } from 'lucide-react';

interface NotificationItemProps {
  notification: AppNotificationDto;
  onOpen: (notification: AppNotificationDto) => void;
  compact?: boolean;
}

export function NotificationItem({ notification, onOpen, compact = false }: NotificationItemProps) {
  const unread = isNotificationUnread(notification);

  return (
    <button
      type="button"
      onClick={() => onOpen(notification)}
      className={cn(
        'hover:bg-muted/70 focus-visible:ring-primary/40 relative flex w-full gap-3 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none',
        compact ? 'px-4 py-3' : 'rounded-2xl border p-4 sm:p-5',
        !compact && unread && 'border-primary/20 bg-primary/[0.035]',
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex shrink-0 items-center justify-center rounded-xl',
          compact ? 'h-9 w-9' : 'h-11 w-11',
          unread ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
        )}
        aria-hidden="true"
      >
        <Bell className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-start gap-2">
          <span className={cn('text-foreground min-w-0 flex-1 text-sm', unread ? 'font-semibold' : 'font-medium')}>
            {notification.renderedSubject?.trim() || 'Nova notificação'}
          </span>
          {unread ? <span className="bg-primary mt-1.5 h-2 w-2 shrink-0 rounded-full" aria-label="Não lida" /> : null}
        </span>
        <span className={cn('text-muted-foreground mt-1 block text-sm', compact ? 'line-clamp-2' : 'leading-relaxed')}>
          {notification.renderedMessage}
        </span>
        <span className="text-muted-foreground mt-2 block text-xs first-letter:uppercase">
          {formatNotificationDate(notification.createdAt)}
        </span>
      </span>
    </button>
  );
}
